import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Box, Text } from "@mantine/core";
import { notifyError, notifySuccess } from "../../../utils/notify";
import { useSalon } from "../../../context/SalonContext";
import { useProducts } from "../../../context/ProductsContext";
import { useClientGroups } from "../../../hooks/useClientGroups";
import { SalesTable, SalesTableRow } from "../../sales/shared/SalesTable";
import { useScreenOverlay } from "../../sales/shared/useScreenOverlay";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { ClientGroup, ClientGroupType } from "../../../types/clientGroups";
import { RuleLabels, composeDefinition } from "./ruleModel";
import GroupTypeCell from "./GroupTypeCell";
import ClientGroupsToolbar, {
  DEFAULT_FILTERS,
  GroupFilters,
} from "./ClientGroupsToolbar";
import ClientGroupPanel from "./ClientGroupPanel";
import ClientGroupEditor from "./ClientGroupEditor";
import EditRulesModal from "./EditRulesModal";
import ManageMembersModal from "./ManageMembersModal";
import { useClientGroupsDrawerSlot } from "./drawerSlot";
import sx from "../../sales/shared/sales.module.css";
import cg from "./clientGroups.module.css";

// Group name column narrowed and Updated widened so the date sits on one line
// and the name ellipsises (per feedback). GROUP / TYPE / MEMBERS / DEFINITION /
// UPDATED / chevron.
const COLUMN_TEMPLATE = "1.3fr 96px 84px 1.6fr 128px 44px";
const TABLE_MIN_WIDTH = 720;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

interface Props {
  /** Type chosen from the shell's `+ New Client Group ▾` menu; opens the editor. */
  requestNewType?: ClientGroupType | null;
  onNewTypeConsumed?: () => void;
}

export const ClientGroupsList: React.FC<Props> = ({
  requestNewType,
  onNewTypeConsumed,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { services, employees } = useSalon() as any;
  const { products } = useProducts() as any;
  const { listGroups, deleteGroup: apiDelete } = useClientGroups();
  // Always displace (never overlay): in overlay mode the panel is
  // position:absolute inside the sticky, overflow:hidden drawer column, which
  // collapses to 0 width when its only child leaves the flow and clips the
  // panel to nothing (the drawer flashed in/out, then vanished). Keeping it
  // in-flow renders the 404px panel reliably.
  const { wrapperRef } = useScreenOverlay(1200);
  const drawerSlot = useClientGroupsDrawerSlot();

  const [groups, setGroups] = useState<ClientGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<GroupFilters>(DEFAULT_FILTERS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorType, setEditorType] = useState<ClientGroupType | null>(null);
  const [editRules, setEditRules] = useState<ClientGroup | null>(null);
  const [manage, setManage] = useState<ClientGroup | null>(null);
  const [toDelete, setToDelete] = useState<ClientGroup | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setGroups(await listGroups());
    } catch (e: any) {
      notifyError(e?.message || "Failed to load client groups");
    } finally {
      setLoading(false);
    }
  }, [listGroups]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Open the editor when the shell's New Client Group menu picks a type.
  useEffect(() => {
    if (requestNewType) {
      setEditorType(requestNewType);
      onNewTypeConsumed?.();
    }
  }, [requestNewType, onNewTypeConsumed]);

  // Deep link: /admin/marketing/groups/:id redirects here with ?group=<id>.
  useEffect(() => {
    const g = searchParams.get("group");
    if (g) setSelectedId(g);
  }, [searchParams]);

  const labels: RuleLabels = useMemo(
    () => ({
      services: Object.fromEntries(
        (services || []).map((s: any) => [
          String(s.uuid ?? s.id ?? s.salon_service_id),
          s.name,
        ]),
      ),
      products: Object.fromEntries(
        (products || []).map((p: any) => [String(p.id), p.name]),
      ),
      staff: Object.fromEntries(
        (employees || []).map((e: any) => [
          String(e.id ?? e.staff_user_id),
          e.name || `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim(),
        ]),
      ),
    }),
    [services, products, employees],
  );

  const defOf = useCallback(
    (g: ClientGroup) =>
      g.group_type === "static"
        ? "Added manually"
        : composeDefinition(g.rules, labels),
    [labels],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = Date.now();
    return groups.filter((g) => {
      if (filters.type !== "all" && g.group_type !== filters.type) return false;
      const mc = g.member_count ?? 0;
      if (filters.size === "over100" && mc <= 100) return false;
      if (filters.size === "under100" && mc >= 100) return false;
      if (filters.size === "empty" && mc !== 0) return false;
      if (filters.updated !== "any") {
        const days = filters.updated === "7d" ? 7 : 30;
        if (now - new Date(g.updated_dt).getTime() > days * 864e5) return false;
      }
      if (q) {
        const hay = `${g.name} ${defOf(g)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [groups, filters, query, defOf]);

  const selected = groups.find((g) => g.id === selectedId) ?? null;

  const closePanel = () => {
    setSelectedId(null);
    if (searchParams.get("group")) {
      searchParams.delete("group");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const onCreated = async (g: ClientGroup) => {
    setEditorType(null);
    await refresh();
    setSelectedId(g.id);
    notifySuccess("Group created");
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDelete(toDelete.id);
      if (selectedId === toDelete.id) setSelectedId(null);
      setToDelete(null);
      await refresh();
      notifySuccess("Group deleted");
    } catch (e: any) {
      notifyError(e?.message || "Failed to delete group");
    }
  };

  const headers = [
    { label: "GROUP" },
    { label: "TYPE" },
    { label: "MEMBERS", align: "right" as const },
    { label: "DEFINITION" },
    { label: "UPDATED" },
    { label: "" },
  ];

  return (
    <>
      {/* list + panel row — breaks out of the content-pane padding so the panel
          spans the whole pane; the list re-pads itself (§8). */}
      <Box className={sx.screenWrapper} ref={wrapperRef}>
        <Box className={sx.listPane}>
          <ClientGroupsToolbar
            query={query}
            onQueryChange={setQuery}
            filters={filters}
            onFiltersChange={setFilters}
            count={filtered.length}
            total={groups.length}
          />
          <SalesTable
            columnTemplate={COLUMN_TEMPLATE}
            minWidth={TABLE_MIN_WIDTH}
            headers={headers}
            loading={loading}
            isEmpty={filtered.length === 0}
            emptyText={
              groups.length === 0
                ? "No client groups yet."
                : "No groups match this search."
            }
          >
            {filtered.map((g) => (
              <SalesTableRow
                key={g.id}
                active={g.id === selectedId}
                onClick={() => setSelectedId(g.id)}
              >
                <Text className={cg.groupName}>{g.name}</Text>
                <GroupTypeCell type={g.group_type} />
                <Text className={`${sx.cellNum} ${sx.cellRight}`}>
                  {g.member_count == null
                    ? "—"
                    : g.member_count.toLocaleString()}
                </Text>
                <Text className={`${sx.cellMuted} ${sx.cellEllipsis}`}>
                  {defOf(g)}
                </Text>
                <Text
                  className={`${sx.cellMuted} ${sx.cellNum}`}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {fmtDate(g.updated_dt)}
                </Text>
              </SalesTableRow>
            ))}
          </SalesTable>
        </Box>
      </Box>

      {/* Panel portals into the shell's full-height drawer column so it spans
          from the top and shrinks the list to the left (like Sales). */}
      {selected &&
        drawerSlot &&
        createPortal(
          <ClientGroupPanel
            group={selected}
            overlay={false}
            onClose={closePanel}
            onEditRules={() => setEditRules(selected)}
            onManageMembers={() => setManage(selected)}
            onDelete={() => setToDelete(selected)}
            refreshKey={refreshKey}
          />,
          drawerSlot,
        )}

      {editorType && (
        <ClientGroupEditor
          initialType={editorType}
          onCancel={() => setEditorType(null)}
          onCreated={onCreated}
        />
      )}

      <EditRulesModal
        opened={!!editRules}
        group={editRules}
        onClose={() => setEditRules(null)}
        onSaved={() => {
          setRefreshKey((k) => k + 1);
          refresh();
        }}
      />

      <ManageMembersModal
        opened={!!manage}
        group={manage}
        onClose={() => setManage(null)}
        onChanged={(count) => {
          setGroups((gs) =>
            gs.map((g) =>
              g.id === manage?.id ? { ...g, member_count: count } : g,
            ),
          );
          setRefreshKey((k) => k + 1);
        }}
      />

      <DeleteConfirmationModal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete client group"
        message={`Delete "${toDelete?.name}"? This can't be undone.`}
      />
    </>
  );
};

export default ClientGroupsList;
